import { Outlet, useLocation } from 'react-router-dom';
import { useEffect, useLayoutEffect, useRef, lazy, Suspense } from 'react';
import { BottomNav } from './BottomNav';
import { ReconnectingBanner } from './ReconnectingBanner';
import { ShellsHud } from './ShellsHud';
import { useStripBottom } from './useStripBottom';
import { useScrollStore } from './scrollStore';
import { useOnline } from '../pwa/useOnline';
import { useCoinFlyer } from './useCoinFlyer';
import { useFlyerQueue } from './flyerQueue';
import { useAchievementCelebrations } from './useAchievementCelebrations';
import { useDailyReceipts } from './useDailyReceipts';
import { useSkillCompletionToast } from './useSkillCompletionToast';
import { useCompanions } from '../api/companions';

// Task 9 (motion system): lazily loaded so the celebration overlay — three rare events, most
// sessions never trigger even one — stays out of the initial chunk. `.then((m) => ({ default:
// m.Celebration }))` because Celebration.tsx exports it as a named export, not a default one.
const Celebration = lazy(() => import('./Celebration').then((m) => ({ default: m.Celebration })));

// Task 15 review fix (IMPORTANT-1): the Decision #10 mustRename gate used to live here, but that
// only ever covered AppShell's own children — /wardrobe/:companionId, /world/battle/:battleId,
// and /world/minigames/:gameCode are router SIBLINGS of AppShell (full-screen takeovers, outside
// the shell on purpose), so a gate here alone never even mounted for a direct deep link into one
// of them. The gate now lives one level up, in RequireHandleClaimed (routes.tsx wraps AppShell
// AND its full-screen siblings in one shared parent using that as its element) — see that
// component's own javadoc for the full reasoning. AppShell goes back to being a plain shell.
export function AppShell() {
  const region = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  // Selectors rather than the whole-store `useScrollStore()`: that form re-rendered AppShell on
  // every `save`, and saves now happen on every scroll event rather than once per navigation.
  const save = useScrollStore(s => s.save);
  const restore = useScrollStore(s => s.restore);
  const isOnline = useOnline();

  /* THE COIN FLYER'S ONE CALL SITE (`docs/DESIGN.md` §21). `useCoinFlyer`'s own contract requires
     this: it removes any still-flying node when the component owning the hook unmounts, so the
     hook has to live on a persistent, shell-level component — this one, which already mounts
     `ShellsHud`, i.e. the flyer's own destination. A hook called from the shop's detail sheet
     instead would cut every flight off the instant that sheet closed, which is the same moment the
     server's response arrives.

     `drain()` reads and CLEARS (see `flyerQueue.ts`), which is what makes this safe under
     `<StrictMode>`'s double-invoked effects: the second invocation gets an empty list instead of
     launching every queued flight a second time. It is called through `getState()` rather than a
     subscribed selector so that draining — which writes to the store — cannot itself be a
     dependency that re-triggers this effect. */
  const { launch } = useCoinFlyer();
  const pendingFlights = useFlyerQueue((s) => s.pending);
  useEffect(() => {
    if (pendingFlights.length === 0) return;
    for (const flight of useFlyerQueue.getState().drain()) launch(flight);
  }, [pendingFlights, launch]);

  /* Two of the three interrupt-tier producers (first-time badge, collection complete). Mounted
     here rather than on the badges/collections screens so the diff sees an award the moment it
     lands, not only while the player happens to be looking at that screen. See the hook's own doc
     comment for the monotonic-flag reasoning and the first-load guard. The level-up producer is
     `HabitatScreen`'s, where the companion whose level changed is observed. */
  useAchievementCelebrations();

  /* The dailies receipt toast (player review 2026-09-27): batches every daily that flipped
     false→true since the last look into one toast, after the async grant lands. See the hook's
     own doc comment for the module-level-state and batching reasoning. */
  useDailyReceipts();

  /* The skill-session completion toast (skill-craft UI polish, spec §3.6), keyed on the ACTIVE
     companion — the one whose sessions the Habitat and the skill screens show. `useCompanions()`
     is the shell's own read of that (react-query dedupes it with Habitat/Me's), and the hook
     itself stays keyed on the id argument so the module-level baseline is per companion, not per
     shell. See the hook's doc comment for the unsealed baseline and where the verb comes from. */
  const companions = useCompanions();
  const activeCompanion = companions.data?.find((c) => c.isActive) ?? companions.data?.[0];
  useSkillCompletionToast(activeCompanion?.id);
  // First-pet flow (SCREENS.md /companions/new "first-pet"): no dock and no Shells chip while the
  // account has no companion — every other tab assumes one. Adding a second pet keeps the shell.
  const bare = pathname === '/companions/new' && companions.data?.length === 0;

  /* Which screen `.content-region`'s CURRENT scroll offset belongs to. Written in a LAYOUT
     effect, so it already names the incoming screen before the browser can dispatch the scroll
     event that a shorter incoming screen's clamp provokes (scroll events are dispatched at frame
     time; layout effects run synchronously in the same task as the DOM swap). Without it, that
     clamp event is delivered to whichever listener is attached at frame time and would record
     0 as the OUTGOING screen's position — the same value-destroying race the save half used to
     lose to, just moved. */
  const owner = useRef(pathname);
  useStripBottom(pathname); // the short tier's rail hugs the strip's measured bottom (see the hook)
  useLayoutEffect(() => { owner.current = pathname; }, [pathname]);

  /* PER-PATHNAME SCROLL SAVE/RESTORE.

     THE BUG THIS SHAPE EXISTS TO PREVENT (measured 2026-08-20, real browser at Pixel 7): this
     effect used to save in its own cleanup — `return () => save(pathname, el.scrollTop)`. A
     passive effect's destroy runs AFTER React has already mutated the DOM for the next render,
     so by then `.content-region` held the INCOMING screen's content. Navigating /me (scrollTop
     120, scrollHeight 1351) → /world (scrollHeight 839, not scrollable at all) meant the browser
     had already clamped scrollTop to 0 when the region shrank, and the cleanup dutifully saved
     that 0 — instrumenting the live element caught the destroy reading 0 against scrollHeight 839.
     The offset was destroyed at save time; the restore that read that 0 back then wrote it into a
     region ALREADY tall enough (scrollHeight 1351) to have held 120, so the restore was working
     correctly on a value already lost. No cleanup form fixes this — layout-effect
     destroys run child-first too, so the Outlet subtree is swapped before AppShell's own hook
     unmounts. The only reliable capture point is while the screen is still mounted, so the
     position is recorded live from the region's own scroll events instead.

     The restore half additionally defers: on a screen whose content arrives after commit (a
     react-query cache entry gc'd since the last visit renders its loading state, images/lazy
     content settle a frame later), the region is still short when this effect runs and the write
     clamps exactly the same way. So an unmet target is held pending and re-applied as the content
     grows, then the observers disconnect. `pending` also gates the save listener — an
     intermediate clamped write is ours, not the player's, and must not overwrite the target. */
  useEffect(() => {
    const el = region.current;
    if (!el) return;

    const target = restore(pathname);
    let pending = target > 0;
    let applied = -1; // the last offset WE wrote, to tell our own writes from the player's

    const stopPending = () => {
      pending = false;
      growth.disconnect();
      childList.disconnect();
    };
    const applyTarget = () => {
      el.scrollTop = target;
      applied = el.scrollTop; // what the browser accepted, which is 0 while the region is short
      if (applied >= target - 1) stopPending(); // landed (scrollTop can be fractional)
    };

    // ResizeObserver watches the CHILDREN, not the region: the region is `flex: 1` inside a
    // 100dvh shell, so its own box never changes size when the content inside it grows.
    const growth = new ResizeObserver(() => { if (pending) applyTarget(); });
    const observeChildren = () => { for (const child of el.children) growth.observe(child); };
    const childList = new MutationObserver(() => {
      if (!pending) return;
      observeChildren();
      applyTarget();
    });

    applyTarget();
    if (pending) {
      observeChildren();
      childList.observe(el, { childList: true });
    }

    const onScroll = () => {
      if (owner.current !== pathname) return; // a clamp provoked by navigating AWAY from here
      if (pending) {
        // Distinguishing our own writes from the player's by POSITION rather than by input event
        // matters: `touchstart`/`wheel` would also fire on an ordinary tap during load and throw
        // away a restore the player never asked to cancel. Growing content never moves scrollTop
        // on its own, so a position we did not write can only be the player scrolling.
        if (el.scrollTop === applied) return;
        stopPending(); // they took over mid-restore — their position wins from here
      }
      save(pathname, el.scrollTop);
    };
    el.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      growth.disconnect();
      childList.disconnect();
      el.removeEventListener('scroll', onScroll);
    };
  }, [pathname, restore, save]);

  return (
    <div className={bare ? 'shell shell-bare' : 'shell'}>
      {!isOnline && <ReconnectingBanner />}
      {!bare && <ShellsHud />}
      <div ref={region} className="content-region"><Outlet /></div>
      {!bare && <BottomNav />}
      {/* Task 9: mounted inside AppShell, not App.tsx (unlike Toast, which every full-screen
          route needs too) — a celebration must never play over battle/minigames/wardrobe, and
          AppShell's own `pathname` (already read above for scroll save/restore) is exactly the
          gate Celebration needs for that. See Celebration.tsx's own header comment for why the
          full pathname list still covers all three routes even though battle/minigames are
          already structurally absent here (router siblings of AppShell, not children). */}
      <Suspense fallback={null}><Celebration blockedBy={pathname} /></Suspense>
    </div>
  );
}
